import { selectTagsStandardProps } from "@/utils/componentUtils";
import { Button, Card, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useFilterSearchParams } from "../../../../common/utils/hooksUtils";
import { URLQuery } from "../../../../common/utils/queryUtils";
import { validationConstants, validations } from "../../../../common/utils/validationUtils";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { InstitutionType, institutionTypeTMap } from "../../enums";
import { INSTITUTION_ROUTE_PATHS } from "../../paths";
import { InstitutionFilterPageRequest } from "../../types";
import InstitutionTypeTag from "../InstitutionTypeTag";

const colSpan = 6;

export const InstitutionFilterView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParams = useFilterSearchParams<InstitutionFilterPageRequest>({ params: ["types"] });

  const [form] = Form.useForm<InstitutionFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      types: searchParams.types && !Array.isArray(searchParams.types) ? [searchParams.types] : searchParams.types,
    });
  }, [searchParams, form]);

  const handleFilterSubmit = (filter: InstitutionFilterPageRequest) => {
    navigate({ search: URLQuery.searchParamsToString({ searchParams: { ...filter } }) }, { replace: true });
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("institution.titles.list")}</h2>}
      extra={
        <ComponentWithPermission permissions={[Permission.ADMIN_GLOBAL_ENUMERATIONS]}>
          <Link to={generatePath(INSTITUTION_ROUTE_PATHS.create.to)}>
            <Button type="primary" icon={<AntIcon type="plus" />}>
              {t("institution.actions.createInstitution")}
            </Button>
          </Link>
        </ComponentWithPermission>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="institutionFilterViewForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item
              name="keyword"
              label={t("common.searchKeyword")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("institution.helpers.searchHint")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item name="types" label={t("institution.enums.institutionType._label")} rules={[validations.none]}>
              <Select
                {...selectTagsStandardProps(institutionTypeTMap)}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                tagRender={(props) => (
                  <InstitutionTypeTag
                    type={props.value}
                    closable={props.closable}
                    onClose={props.onClose}
                    label={props.label}
                  />
                )}
                options={Object.values(InstitutionType).map((type) => ({
                  value: type,
                  label: <InstitutionTypeTag type={type} />,
                }))}
              />
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
